import { Currency, DistanceUnit, LogLevel, RibbonData, RouteCache, TrackingData } from '@hdms/shared/common-types';

export interface CartData {
  cartAllProducts: CartProductTrackingData[];
  smartBar: {
    cancelLabel: string;
    checkoutLabel: string;
    disableCheckoutButton: boolean;
    disablePurchaseRequestButton: boolean;
    disableQuoteButton: boolean;
    isCartEmpty: boolean;
    removeAllLabel: string;
    requestPurchaseLabel: string;
    requestQuoteLabel: string;
    shareButtonLabel: string;
    showCancelButton: boolean;
    showCheckoutButton: boolean;
    showPurchaseRequestButton: boolean;
    showQuoteButton: boolean;
    showShareButton: boolean;
    gamCreateQuoteOrderDisabled: boolean;
  };
  removeAllModal: {
    description: string;
    removeAllUrl: string;
    title: string;
  };
  replacementsModal: {
    itemNumberLabel: string;
    productDetailLabel: string;
    replaceLabel: string;
    replacementSuggestionsLabel: string;
  };
  quickItemEntryModal: {
    addToCartFormId: string;
    contactUsLinkTextLabel: string;
    errorMessage: string;
    modalDescriptionText: string;
    modalTitle: string;
    successMessage: string;
    textareaCaption: string;
    textareaId: string;
    textareaPlaceholder: string;
    textareaValue: string;
    warningMessage: string;
  };
}

export interface CartProductTrackingData {
  brand?: string;
  category?: string;
  dimension55?: string;
  id?: string;
  name?: string;
  price?: string;
  quantity?: string;
  variant?: string;
}

export interface CategoryData {
  isCategoryPage: boolean;
  isCategoryFirstLevel: boolean;
  categoryTeasers?: {
    name: string;
    description: string;
    imageUrl: string;
    href: string;
  }[];
}

export interface PhoneData {
  countryCode: string;
}

export interface CountryLanguage {
  language: string;
  label?: string;
  locale?: string;
  languageTag?: string;
  languageTagLong?: string;
  didomiLanguage?: string;
  rtl?: boolean;
}

export interface DownloadCenter {
  ribbonData: RibbonData;
}

export enum MobileAppBiometric {
  FINGERPRINT = 'fingerprint',
  FACE = 'face',
  NONE = 'none'
}

export interface MobileAppParameters {
  version: string;
  biometric?: MobileAppBiometric;
}

export interface RapidPayConfig {
  url?: string;
  isECheque?: boolean;
  merchantName?: string;
}

export enum FrontendMode {
  FULL = 'FULL',
  MIXED = 'MIXED',
  NONE = 'NONE',
  SPA = 'SPA'
}

export enum HostContext {
  HYBRIS = 'HYBRIS',
  AEM = 'AEM'
}

export enum DevBuild {
  HYBRIS = 'HYBRIS',
  NGC = 'NGC'
}

export type CustomLinks = Record<string, string>;

export interface RawEnv extends Partial<CountryLanguage> {
  // indicates an Angular production build
  production?: boolean;
  // indicates the standalone app running on a prod-like env
  isProdEnv?: boolean;
  devBuild?: DevBuild;
  standalone?: boolean;
  releaseVersion?: string;
  // server specific props only
  server?: {
    logLevel?: LogLevel | keyof typeof LogLevel;
    anonymousToken?: string;
    featureToggles?: Record<string, any>;
    translationPath?: string;
    routeCache?: RouteCache;
    googleTagManagerId?: string;
    didomiApiKey?: string;
  };
  logLevel?: LogLevel | keyof typeof LogLevel;
  country?: string;
  languages?: CountryLanguage[];
  hub?: string;
  resourcePath?: string;
  featureToggles?: Record<string, any>;
  anonymousToken?: string;
  userBearerToken?: string;
  translationPath?: string;
  gqlEndpoint?: string;
  evoucherEndpointUrl?: string;
  cloudBaseUrl?: string;
  dusBaseUrl?: string;
  aemEndpointUrl?: string;
  googleMapsClientId?: string;
  dateFormat?: string;
  hqTimeZone?: string;
  currency?: Currency;
  currencies?: Currency[];
  mobileAppParams?: MobileAppParameters;
  hostContext?: HostContext;
  compressedLayout?: boolean;
  oauthClientRedirectHost?: string;
  isByDesign?: boolean;
  liveChatJsUrl?: string;
  liveChatDeploymentId?: string;
  liveChatRecaptchaV3PublicKey?: string;
  phone?: { countryCode: string };
  distanceUnit?: DistanceUnit;
  recaptchaV3PublicKey?: string;
  salesOrgId?: string;
  cardTokenizationV1EndpointUrl?: string;
  cardTokenizationV2EndpointUrl?: string;
  rapidPayConfig?: RapidPayConfig;
  customLinks?: CustomLinks;
}

/**
 * The below type definitions contain all common props provided by Universal but also in __env.hdms
 */

type CommonMoProps = Pick<RawEnv, 'country' | 'language' | 'locale' | 'dateFormat' | 'isByDesign'>;
type CommonEnvProps = Pick<RawEnv, 'translationPath' | 'resourcePath' | 'gqlEndpoint' | 'googleMapsClientId'>;
type SharedProps = CommonMoProps & CommonEnvProps;

/**
 * The interface contains all props not provided as part of RawEnv coming from universal
 * The props are differentiated in
 * - already being migrated to Universal but with a different name or coming from different sources
 * - still pending migration to Universal
 */
export interface LegacyEnv extends SharedProps {
  // already migrated:
  currencyPattern?: string;
  currencyCode?: string;
  defaultCurrencySymbol?: string;
  loggedIn?: boolean;
  isTsLogin?: boolean;
  fleetContract?: boolean;
  mobileApp?: boolean;
  frontendMode?: FrontendMode;
  tsShipToID?: string;
  rapidPaySorg?: string;
  rapidPayUrl?: string;
  merchantName?: string;
  region?: string;
  // still to be migrated:
  trackingData?: TrackingData;
  cartData?: CartData;
  categoryData?: CategoryData;
  searchResults?: object;
  downloadCenter?: DownloadCenter;
  isAccountSync?: boolean;
}

interface WindowEnvProps {
  __env?: {
    hdms: LegacyEnv;
  };
  hdmsBuildVersion: string;
  hdmsReleaseVersion: string;
}

export type EnvWindow = Window & WindowEnvProps;
